import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { Users, TrendingUp } from "lucide-react";
import { ClientPaymentTable } from "./components/ClientPaymentTable";
import { calculateCompanyTotal } from "./utils/paymentCalculations";
import { SearchInput } from "../SearchInput";

interface PaymentPlan {
  id: string;
  amount_agreed: number;
  first_payment_date: string;
  amount_paid: number;
  clients: {
    id: number;
    name: string;
    status: string;
  } | null;
}

interface PaymentSummary {
  clientId: number;
  clientName: string;
  totalAgreed: number;
  companyTotal: number;
  firstPaymentDate: string;
  status: string;
  currentMonth: number;
}

export const ClientPaymentOverview = () => {
  const [potentialClients, setPotentialClients] = useState<PaymentSummary[]>([]);
  const [activeClients, setActiveClients] = useState<PaymentSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('payment_plans')
          .select(`
            id,
            amount_agreed,
            first_payment_date,
            amount_paid,
            clients (
              id,
              name,
              status
            )
          `)
          .order('first_payment_date', { ascending: false });

        if (error) throw error;

        const paymentPlans = data as unknown as PaymentPlan[];
        
        const today = new Date();
        const processedData = paymentPlans.map(plan => {
          const startDate = new Date(plan.first_payment_date);
          const monthDiff = (today.getFullYear() - startDate.getFullYear()) * 12 + 
                          (today.getMonth() - startDate.getMonth()) + 1;
          
          return {
            clientId: plan.clients?.id || 0,
            clientName: plan.clients?.name || 'Unknown Client',
            totalAgreed: Number(plan.amount_agreed) || 0,
            companyTotal: calculateCompanyTotal(
              Number(plan.amount_agreed), 
              monthDiff,
              plan.clients?.status || 'Pending'
            ),
            firstPaymentDate: plan.first_payment_date,
            status: plan.clients?.status || 'Pending',
            currentMonth: monthDiff > 0 ? monthDiff : 1
          };
        });

        setPotentialClients(processedData.filter(client => 
          client.status === 'Pending' || client.status === 'Contacted'
        ));
        setActiveClients(processedData.filter(client => 
          client.status === 'Active'
        ));

      } catch (error: any) {
        console.error('Error fetching payment data:', error);
        toast({
          variant: "destructive",
          title: "Error fetching payment data",
          description: error.message
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentData();
  }, [toast]);

  const filteredPotentialClients = potentialClients.filter(client =>
    client.clientName.includes(searchQuery)
  );

  const filteredActiveClients = activeClients.filter(client =>
    client.clientName.includes(searchQuery)
  );

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading payment data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search clients..."
        />
      </div>

      {/* Potential Clients */}
      <Card className="p-6 bg-[#222222] border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-blue-400" />
          <h2 className="text-xl font-bold text-white">Potential Client Payments</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">Client</th>
                <th className="text-right p-4 text-gray-400 font-medium">Total Amount</th>
                <th className="text-right p-4 text-gray-400 font-medium">Company Revenue</th>
                <th className="text-center p-4 text-gray-400 font-medium">Month</th>
                <th className="text-center p-4 text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              <ClientPaymentTable clients={filteredPotentialClients} type="potential" />
            </tbody>
          </table>
        </div>
      </Card>

      {/* Active Clients */}
      <Card className="p-6 bg-[#222222] border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-green-400" />
          <h2 className="text-xl font-bold text-white">Active Payment Plans</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">Client</th>
                <th className="text-right p-4 text-gray-400 font-medium">Total Amount</th>
                <th className="text-right p-4 text-gray-400 font-medium">Company Revenue</th>
                <th className="text-center p-4 text-gray-400 font-medium">Month</th>
                <th className="text-center p-4 text-gray-400 font-medium">Start Date</th>
                <th className="text-center p-4 text-gray-400 font-medium">Remarks</th>
              </tr>
            </thead>
            <tbody>
              <ClientPaymentTable clients={filteredActiveClients} type="active" />
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
